import { useContext, useEffect, useRef } from 'react'
import moment from 'moment'
import { SchedulingWizardContext } from 'app/components/scheduling/SchedulingWizard'

export type BookingDetails = {
  dayLabel: string
  timeLabel: string
  providerName?: string
}

/**
 * Reports a completed booking out of the wizard. Must be called from a
 * component rendered as a child of <SchedulingWizard> (i.e. within its context).
 *
 * Fires at most once per mount: `createdAppointment` stays set on the context
 * through the confirmation step, so without the ref this would re-fire whenever
 * a dependency's identity changes on a later render (for example `providers`
 * getting a new array identity from a refetch after booking), not just once.
 */
function useBookingReport({
  startDate,
  onBooked,
}: {
  startDate?: string
  onBooked: (details: BookingDetails) => void
}): void {
  const { createdAppointment, providers, provider, selectedAvailability, selectedDaySlotIndex, selectedTimeSlot } =
    useContext(SchedulingWizardContext)
  const reportedRef = useRef(false)

  useEffect(() => {
    if (!createdAppointment || reportedRef.current) return
    reportedRef.current = true
    const bookedProvider = providers.find((p) => p.id === selectedAvailability?.providerId)
    // moment(undefined) resolves to now and is still .isValid(), so an unguarded
    // derivation here would yield a plausible-looking wrong date instead of an
    // empty one when startDate is missing or selectedDaySlotIndex is unset
    // (-1). buildBookingDraft only rejects a falsy dayLabel, so a wrong date
    // would otherwise sail straight into a text message to a patient.
    const dayLabel =
      startDate && selectedDaySlotIndex >= 0
        ? moment(startDate).add(selectedDaySlotIndex, `days`).format(`ddd, MMM D`)
        : ``
    onBooked({
      dayLabel,
      timeLabel: selectedTimeSlot?.time ?? ``,
      providerName: bookedProvider?.displayName ?? provider?.displayName,
    })
  }, [
    createdAppointment,
    providers,
    provider,
    selectedAvailability,
    selectedDaySlotIndex,
    selectedTimeSlot,
    startDate,
    onBooked,
  ])
}

/**
 * Null-rendering component that exists only to sit inside <SchedulingWizard>'s
 * context so useBookingReport can read it. Must be rendered as a child of
 * <SchedulingWizard>. All reporting logic lives in useBookingReport above.
 */
export function BookingReporter({
  startDate,
  onBooked,
}: {
  startDate?: string
  onBooked: (details: BookingDetails) => void
}): null {
  useBookingReport({ startDate, onBooked })
  return null
}
